import React, { useMemo, useState } from "react";
import { PiSquaresFour } from "react-icons/pi";
import { useMediaQuery, useTheme } from "@mui/material";

const DEFAULT_IMAGE =
  "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b";

const STATUS_COLOR = {
  ongoing: "#4D6BF6",
  active: "#4D6BF6",
  completed: "#22c55e",
  submitted: "#22c55e",
  delivered: "#16a34a",
  approved: "#22c55e",
  APPROVED: "#22c55e",
  SUBMITTED: "#22c55e",
  pending: "#eab308",
  due: "#eab308",
  IN_REVIEW: "#eab308",
  READY_FOR_INSPECTION: "#eab308",
  proposal: "#eab308",
  ready_to_start: "#a855f7",
  in_warranty: "#854d0e",
  inspection: "#854d0e",
  "awaiting-review": "#854d0e",
  blocked: "#dc2626",
  closed: "#dc2626",
  REJECTED: "#dc2626",
  FAILED: "#dc2626",
  change_request: "#f59e0b",
  request_for_change: "#f59e0b",
  AWAITING_CONTRACTOR_CONFIRMATION: "#d97706",
  "awaiting-contractor-confirmation": "#d97706",
  draft: "#0d0d0e",
};

function PlannedProgress({ completion = 0, type }) {
  const barColor =
    completion >= 100 ? "#22c55e" : STATUS_COLOR[type] || "#4D6BF6";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        width: "60%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          width: "100%",
        }}
      >
        <div
          style={{
            position: "relative",
            minWidth: 60,
            flex: 1,
            height: 8,
            background: "#e5e7eb",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${completion}%`,
              background: barColor,
              borderRadius: 4,
              transition: "width 300ms ease-in-out",
            }}
          />
        </div>
        <span style={{ fontSize: 11, color: "#4b5563" }}>{completion}%</span>
      </div>
    </div>
  );
}

export default function MilestoneCard({
  refDataMilestones = [],
  selectedMilestones = [],
  setSelectedMilestones,
  milestoneProgress = {},
  onProgressChange,
  milestoneNotes = {},
  onNotesChange,
  disabled = false,
  activities = [],
  isLoading = false,
  milestoneIdsFromImages = [],
  isDisabled = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const refDataByMilestoneId = useMemo(() => {
    const map = {};
    (refDataMilestones || []).forEach((m) => {
      if (m.id == null) return;
      map[m.id] = m;
      if (typeof m.id === "number") map[String(m.id)] = m;
      else if (typeof m.id === "string") map[Number(m.id)] = m;
    });
    return map;
  }, [refDataMilestones]);

  const refDataIds = useMemo(
    () =>
      new Set(
        (refDataMilestones || [])
          .map((m) => m.id)
          .filter((id) => id != null)
      ),
    [refDataMilestones]
  );

  const getProgressInfo = (item) =>
    refDataByMilestoneId[item.id] ??
    refDataByMilestoneId[Number(item.id)] ??
    refDataByMilestoneId[String(item.id)];

  const itemName = (item) =>
    item.activity_name ?? item.milestone_name ?? item.name ?? "";
  const itemTags = (item) => item.tags ?? item.tag ?? "-";
  const imageSrc = (item) =>
    item?.milestone_image?.path ?? item?.image?.path ?? DEFAULT_IMAGE;

  const [lightboxImage, setLightboxImage] = useState(null);

  const handleCheckbox = (item) => {
    if (disabled) return;
    if (refDataIds.has(item.id)) return;
    const isSelected = selectedMilestones.some((m) => m.id === item.id);
    if (isSelected) {
      setSelectedMilestones?.((prev) => prev.filter((m) => m.id !== item.id));
    } else {
      setSelectedMilestones?.((prev) => [...prev, item]);
    }
  };

  const handleProgressChange = (id, value, prev) => {
    const safe = Math.max(value, prev);
    onProgressChange?.(id, safe);
  };

  const list =
    Array.isArray(activities) && activities.length > 0 ? activities : [];

  if (isLoading) {
    return (
      <div style={{ width: "100%" }}>
        <p style={{ fontWeight: 500, marginBottom: 12, fontFamily: "Inter Tight" }}>
          Impacted Milestones
        </p>
        <div style={{ textAlign: "center", padding: 32, color: "#6b7280" }}>
          Loading activities...
        </div>
      </div>
    );
  }

  if (list.length === 0) {
    const message =
      isDisabled && Array.isArray(activities)
        ? "Select a scope to see impacted milestones (activities)."
        : "No activities found for this scope.";
    return (
      <div style={{ width: "100%" }}>
        <p style={{ fontWeight: 500, marginBottom: 12, fontFamily: "Inter Tight" }}>
          Impacted Milestones
        </p>
        <div style={{ textAlign: "center", padding: 32, color: "#6b7280", fontSize: 13 }}>
          {message}
        </div>
      </div>
    );
  }

  const renderSliderRow = (item, currentValue, prev) => {
    const trackBg = `linear-gradient(to right, #22c55e 0%, #22c55e ${prev}%, #2563eb ${prev}%, #2563eb ${currentValue}%, #bfc5ce40 ${currentValue}%, #bfc5ce40 100%)`;
    const lockedLeft = currentValue <= prev;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div className="pud-slider-wrap">
          <div
            className="pud-slider-track"
            style={{ background: trackBg }}
            aria-hidden
          />
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={currentValue}
            disabled={disabled}
            onChange={(e) =>
              handleProgressChange(item.id, Number(e.target.value), prev)
            }
            className="pud-slider"
            style={lockedLeft ? { cursor: "not-allowed" } : undefined}
          />
        </div>
        <span className="pud-slider-value">{currentValue}%</span>
      </div>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <p style={{ fontWeight: 500, marginBottom: 12, fontFamily: "Inter Tight" }}>
        Impacted Milestones
      </p>

      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {list.map((item) => {
            const isChecked =
              selectedMilestones.some((m) => m.id === item.id) ||
              milestoneIdsFromImages.includes(item.id);
            const isLocked = refDataIds.has(item.id);
            const info = getProgressInfo(item);
            const inRef = !!info;
            const currentValue = inRef
              ? milestoneProgress[item.id] ?? info?.progress ?? 0
              : milestoneProgress[item.id] ??
                item.progress ??
                item.milestone_percentage ??
                0;
            const prev = inRef
              ? info?.previous_progress ?? info?.progress ?? 0
              : item.progress ?? item.milestone_percentage ?? 0;

            return (
              <div
                key={item.id}
                style={{
                  background: "#fff",
                  padding: "12px",
                  borderBottom: "1px solid #d7d8d9",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isLocked}
                    readOnly={isLocked}
                    onChange={() => handleCheckbox(item)}
                    style={{ width: 16, height: 16, cursor: isLocked ? "default" : "pointer" }}
                  />
                  <span style={{ fontSize: 13, color: "#949daa", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <PiSquaresFour size={18} /> {itemTags(item)}
                  </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <img
                      src={imageSrc(item)}
                      alt=""
                      className="pud-milestone-thumb"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        item?.milestone_image?.path &&
                        setLightboxImage(item.milestone_image.path)
                      }
                    />
                    <span style={{ fontSize: 14, color: "#0d0d0e", fontWeight: 500 }}>
                      {itemName(item)}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end", minWidth: 120 }}>
                    <label style={{ fontSize: 11, color: "#949daa" }}>Planned Progress</label>
                    <PlannedProgress
                      completion={item.milestone_percentage ?? 0}
                      type={item.status}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: 12, color: "#949daa" }}>Progress</label>
                  {renderSliderRow(item, currentValue, prev)}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ fontSize: 12, color: "#949daa" }}>Additional Notes</label>
                  <input
                    type="text"
                    placeholder="Notes (optional)"
                    value={milestoneNotes[item.id] ?? ""}
                    disabled={disabled}
                    onChange={(e) => onNotesChange?.(item.id, e.target.value)}
                    className="pud-input"
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "32px 2fr 2fr 3fr 3fr",
              gap: 12,
              padding: "12px 0",
              fontSize: 13,
              color: "#949daa",
            }}
          >
            <div />
            <div>Milestone</div>
            <div>Planned Progress</div>
            <div>Progress</div>
            <div>Additional Notes</div>
          </div>
          <div className="pud-milestone-table">
            {list.map((item) => {
              const isChecked =
                selectedMilestones.some((m) => m.id === item.id) ||
                milestoneIdsFromImages.includes(item.id);
              const isLocked = refDataIds.has(item.id);
              const info = getProgressInfo(item);
              const inRef = !!info;
              const currentValue = inRef
                ? milestoneProgress[item.id] ?? info?.progress ?? 0
                : milestoneProgress[item.id] ??
                  item.progress ??
                  item.milestone_percentage ??
                  0;
              const prev = inRef
                ? info?.previous_progress ?? info?.progress ?? 0
                : item.progress ?? item.milestone_percentage ?? 0;

              return (
                <div
                  key={item.id}
                  className="pud-milestone-row"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "32px 2fr 2fr 3fr 3fr",
                    gap: 12,
                    alignItems: "center",
                    padding: "12px 0",
                    minWidth: 0,
                  }}
                >
                  <div>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isLocked}
                      readOnly={isLocked}
                      onChange={() => handleCheckbox(item)}
                      style={{ width: 16, height: 16, cursor: isLocked ? "default" : "pointer" }}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                    <img
                      src={imageSrc(item)}
                      alt=""
                      className="pud-milestone-thumb"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        item?.milestone_image?.path &&
                        setLightboxImage(item.milestone_image.path)
                      }
                    />
                    <div style={{ fontSize: 14, color: "#0d0d0e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {itemName(item)}
                    </div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <PlannedProgress
                      completion={item.milestone_percentage ?? 0}
                      type={item.status}
                    />
                  </div>
                  <div style={{ minWidth: 0, marginRight: 20 }}>
                    {renderSliderRow(item, currentValue, prev)}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <input
                      type="text"
                      placeholder="Notes (optional)"
                      value={milestoneNotes[item.id] ?? ""}
                      disabled={disabled}
                      onChange={(e) => onNotesChange?.(item.id, e.target.value)}
                      className="pud-input"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            cursor: "pointer",
          }}
        >
          <img src={lightboxImage} alt="" style={{ maxWidth: "90%", maxHeight: "90%" }} />
        </div>
      )}
    </div>
  );
}
